import React, { FunctionComponent, useEffect, useState } from 'react'
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'

import { getCuratorStats } from 'Redux/actions/reviewsActions'
import { activateCurator, sendEmailToConfirmAccount } from 'Redux/actions/auth'
import { locationEvent, pageInfo, pageViewEvent } from 'Helpers/tagManager'
import Alert from 'Shared/components/Alert'
import { TermsAndConditions } from 'Shared/components'

import EmailNotConfirmedAlert from 'Containers/CampaignsList/components/EmailNotConfirmedAlert/EmailNotConfirmedAlert'
import {
  getIsEmailBounced,
  getUserSelector,
  getIsNewTerm,
} from 'Redux/selectors/auth'
import { InfoCard, TrackList, Modal, AccountReactivation } from './components'
import { CuratorStats } from './props'
import {
  Container,
  PageTitle,
  InfoCardsOverflow,
  InfoCardsContainer,
  AlertText,
  AlertTitle,
  AlertButton,
} from './styles'

const Reviews: FunctionComponent = () => {
  const [visibleContent, setVisibleContent] = useState(true)
  const [isVisibleTrackList, setIsVisibleTable] = useState(true)
  const [activatingModal, setActivatingModal] = useState(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const user = useSelector(getUserSelector)

  useEffect(() => {
    if (user) {
      pageViewEvent(user, pageInfo.reviews)
      locationEvent()
    }
  }, [user])

  const curatorStats: CuratorStats = useSelector(
    (state: RootStateOrAny) => state.reviews.curatorStats
  )
  const {
    isDeactivated,
    is_email_confirmed,
    is_first_campaign,
    email,
  } = useSelector(getUserSelector)

  const isEmailBounced = useSelector(getIsEmailBounced)

  const isNewTerm: boolean = useSelector(getIsNewTerm)

  const isMobile = useMediaQuery({ maxWidth: 576 })

  useEffect(() => {
    dispatch(getCuratorStats())
  }, [])

  const visibleContentMobile = (visibleContent && isMobile) || !isMobile

  const isVisibleTrackListMobile = (isVisibleTrackList && isMobile) || !isMobile

  const toggleActivatingModal = () => {
    setActivatingModal(!activatingModal)
  }

  const onActivateBack = () => {
    dispatch(activateCurator())
    toggleActivatingModal()
  }

  const toggleContent = (value: boolean) => {
    setVisibleContent(value)
    setIsVisibleTable(value)
  }

  const onSendEmailToConfirmAccount = () => {
    dispatch(sendEmailToConfirmAccount())
  }

  return (
    <Container>
      {visibleContentMobile && (
        <>
          <PageTitle>{t('reviewsPage.pageTitle')}</PageTitle>
          {isDeactivated && (
            <Alert color="danger" icon>
              <AlertText>
                <AlertTitle>
                  {t('reviewsPage.accountDeactivatedTitle')}
                </AlertTitle>
                <div>{t('reviewsPage.accountDeactivatedMessage')}</div>
              </AlertText>
              <AlertButton onClick={onActivateBack}>
                {t('reviewsPage.accountDeactivatedButton')}
              </AlertButton>
            </Alert>
          )}
          {((!is_email_confirmed && !is_first_campaign) || isEmailBounced) && (
            <EmailNotConfirmedAlert
              isArtist={false}
              email={email}
              isEmailConfirmd={!!is_email_confirmed}
              isEmailBounced={isEmailBounced}
              sendEmailToConfirmAccount={onSendEmailToConfirmAccount}
            />
          )}

          <InfoCardsOverflow>
            <InfoCardsContainer>
              <InfoCard
                title={t('reviewsPage.requestsReceived')}
                value={curatorStats.receivedCount}
              />
              <InfoCard
                title={t('reviewsPage.reviewsSent')}
                value={curatorStats.reviewedCount}
                subTitle={t('reviewsPage.reviewRate')}
                subValue={curatorStats.reviewRate}
              />
              <InfoCard
                title={t('reviewsPage.addedToPlaylist')}
                value={curatorStats.trackAddedCount}
                subTitle={t('reviewsPage.acceptanceRate')}
                subValue={curatorStats.acceptanceRate}
              />
              <InfoCard
                title={t('reviewsPage.missed')}
                value={curatorStats.missedCanceledCount}
                subTitle={t('reviewsPage.missingRate')}
                subValue={curatorStats.missingRate}
                isDataLabel
              />
            </InfoCardsContainer>
          </InfoCardsOverflow>
        </>
      )}
      {isVisibleTrackListMobile && (
        <TrackList setVisibleContent={setVisibleContent} />
      )}
      {activatingModal && (
        <Modal
          title={t('reviewsPage.reactivationModalHeader')}
          modalHeaderGreen
          isModalVisible={activatingModal}
          closeModal={toggleActivatingModal}
        >
          <AccountReactivation onCloseSucccess={toggleActivatingModal} />
        </Modal>
      )}
      {isNewTerm && <TermsAndConditions toggleContent={toggleContent} />}
    </Container>
  )
}

export default Reviews
